'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

/**
 * 오리지널스 추가 시 ValidOriginalsPath, VALID_ORIGINALS_PATH, buttonContexts를 추가해야 함
 * 1. ValidOriginalsPath: 오리지널스의 경로를 추가
 * 2. VALID_ORIGINALS_PATH: 오리지널스의 경로를 추가
 * 3. buttonContexts: 오리지널스의 이름, 로고 이미지, 버튼 스타일을 추가
 */

interface ButtonContext {
  name: string;
  logoImage: string;
  styles: {
    button: React.CSSProperties;
    text: React.CSSProperties;
  };
}

type ValidOriginalsPath =
  | '/fcseoul-2025'
  | '/fcanyang-2025'
  | '/steelers-2025'
  | '/dhcfc-2025'
  | '/championsrun'
  | '/fcseoul'
  | '/bluewings'
  | '/fcanyang'
  | '/steelers'
  | '/dhcfc'
  | '/uhdfc'
  | '/jbfc'
  | '/doosanbears'
  | '/seoleimrun';

// 가능한 오리지널스 경로
const VALID_ORIGINALS_PATH: ValidOriginalsPath[] = [
  '/fcseoul-2025',
  '/fcanyang-2025',
  '/steelers-2025',
  '/dhcfc-2025',
  '/championsrun',
  '/fcseoul',
  '/bluewings',
  '/fcanyang',
  '/steelers',
  '/dhcfc',
  '/uhdfc',
  '/jbfc',
  '/doosanbears',
  '/seoleimrun',
];

// AdaptiveBackButton이 보여질 페이지의 시작 경로
const VISIBLE_PATH = ['/mypage', '/mypage/entry-history', '/mypage/point', '/notion-app'];

// 자판기 FAQ 페이지: return_originals_url 쿼리 없이 직접 진입 시 기본 복귀 경로
const VENDING_MACHINE_FAQ_PATH = '/notion-app/vending-machine-faq';
const VENDING_MACHINE_FAQ_DEFAULT_RETURN_URL: ValidOriginalsPath = '/uhdfc';

export const RETURN_ORIGINALS_URL_QUERY_KEY = 'return_originals_url';

const buttonContexts: Record<ValidOriginalsPath, ButtonContext> = {
  ['/fcseoul-2025']: {
    name: 'FC서울 팬카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/fcseoul_logo.webp',
    styles: {
      button: {
        border: '1px solid #820009',
        background: 'linear-gradient(180deg, #630209 0%, #8d2d34 70.01%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/fcanyang-2025']: {
    name: 'FC안양 보라카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/fcanyang_logo.webp',
    styles: {
      button: {
        border: '1px solid #b860cc',
        background: 'linear-gradient(180deg, #4f1780 29.99%, #6d26aa 100%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/steelers-2025']: {
    name: '포항 스틸러스 카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/steelers_logo.webp',
    styles: {
      button: {
        border: '1px solid #7A2C32',
        background: 'linear-gradient(180deg, #630209 0%, #8D2D34 70.01%)',
        boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/dhcfc-2025']: {
    name: '대전하나시티즌 DH 카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/dhcfc_logo.webp',
    styles: {
      button: {
        border: '1px solid #008e71',
        background: 'linear-gradient(180deg, #004537 0%, #01624f 100%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/fcseoul']: {
    name: 'FC서울 팬카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/fcseoul_logo.webp',
    styles: {
      button: {
        border: '1px solid #820009',
        background: 'linear-gradient(180deg, #630209 0%, #8d2d34 70.01%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/bluewings']: {
    name: '수원삼성 블루카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/suwonbluewings_logo.webp',
    styles: {
      button: {
        border: '1px solid #1131dc',
        background: 'linear-gradient(180deg, #2036ab 0%, #1954ab 70.01%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/fcanyang']: {
    name: 'FC안양 보라카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/fcanyang_logo.webp',
    styles: {
      button: {
        border: '1px solid #b860cc',
        background: 'linear-gradient(180deg, #4f1780 29.99%, #6d26aa 100%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/steelers']: {
    name: '포항 스틸러스 카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/steelers_logo.webp',
    styles: {
      button: {
        border: '1px solid #7A2C32',
        background: 'linear-gradient(180deg, #630209 0%, #8D2D34 70.01%)',
        boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/dhcfc']: {
    name: '대전하나시티즌 DH 카드',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/dhcfc_logo.webp',
    styles: {
      button: {
        border: '1px solid #008e71',
        background: 'linear-gradient(180deg, #004537 0%, #01624f 100%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/uhdfc']: {
    name: '울산 HD FC 팀카드',
    logoImage: 'https://cdn.kottonseed.io/assets/football/2026/uhdfc/back_button_logo.webp',
    styles: {
      button: {
        border: '1px solid #87B6F8',
        background: 'linear-gradient(180deg, #0F68E5 0%, #003A8E 70.01%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/jbfc']: {
    name: '전북현대모터스 그린카드',
    logoImage: 'https://cdn.kottonseed.io/assets/football/2026/jbfc/back_button_logo.webp',
    styles: {
      button: {
        background: '#29DA35',
        boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  ['/doosanbears']: {
    name: '두산베어스 팬카Doo',
    logoImage: 'https://cdn.kottonseed.io/assets/doosanbears/back_button_logo.webp',
    styles: {
      button: {
        border: '1px solid #586cff',
        background: 'linear-gradient(180deg, #272fa4 0%, #000076 100%)',
        boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  // TODO(fcseoul-run): 전용 32x32 로고 시안 없음 → FC서울 로고 재사용. 시안 나오면 교체
  ['/championsrun']: {
    name: '챔피언스 런',
    logoImage: 'https://cdn.kottonseed.io/assets/icons/logo/fcseoul_logo.webp',
    styles: {
      button: {
        border: '1px solid #ffaab0',
        background: 'linear-gradient(180deg, #d7000f 0%, #b1010e 100%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
  // TODO(seoleimrun): 디자인 확정 후 실제 로고 자산/브랜드색으로 교체
  ['/seoleimrun']: {
    name: '설레임런',
    logoImage: 'https://cdn.kottonseed.io/assets/seoleimrun/back_button_logo.webp',
    styles: {
      button: {
        border: '1px solid #ff9ec4',
        background: 'linear-gradient(180deg, #ff5b9e 0%, #d6347a 100%)',
        boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
      },
      text: {},
    },
  },
};

export const useAdaptiveBackButton = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const returnOriginalsUrl = searchParams?.get(RETURN_ORIGINALS_URL_QUERY_KEY);

  const { isVisible, returnUrl, buttonContext } = useMemo(() => {
    // 자판기 FAQ 페이지는 return_originals_url 쿼리 없이 직접 진입할 수 있으므로 기본 복귀 경로 적용
    if (!returnOriginalsUrl && pathname === VENDING_MACHINE_FAQ_PATH) {
      return {
        isVisible: true,
        returnUrl: VENDING_MACHINE_FAQ_DEFAULT_RETURN_URL,
        buttonContext: buttonContexts[VENDING_MACHINE_FAQ_DEFAULT_RETURN_URL],
      };
    }

    const shouldBeVisible = VISIBLE_PATH.some((path) => pathname?.includes(path));
    const isValidOriginalsPath = VALID_ORIGINALS_PATH.some((path) => returnOriginalsUrl?.includes(path));
    const originalsPath = VALID_ORIGINALS_PATH.find((path) => returnOriginalsUrl?.includes(path));

    const visible = !!(returnOriginalsUrl && shouldBeVisible && isValidOriginalsPath);

    return {
      isVisible: visible,
      returnUrl: visible ? returnOriginalsUrl : '',
      buttonContext: originalsPath ? buttonContexts[originalsPath] : undefined,
    };
  }, [pathname, returnOriginalsUrl]);

  return {
    isVisible,
    returnUrl,
    buttonContext,
  };
};

export default useAdaptiveBackButton;
